import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { UnionServiceType } from "src/api/adminMemo/adminmemo-types";
import { CommonFacilityModel } from "src/api/building/building-types";
import { useApiOperation } from "src/api/hooks";
import { getAllReservationPolicyListAsync, saveReservationPolicyAsync, saveReservationPolicyExclusiveAsync } from "src/api/product/product-api";
import { ReservationPolicySaveParams, StartTimePolicy } from "src/api/product/product-types";
import { BaseButton, BaseCheckbox, BaseRadio, BaseToggle, BaseTooltip } from "src/components";
import useReservationSchedule, { DaySchedule, type WeekSchedule } from "src/pages/product/hooks/useReservationSchedule";
import { SpaceTypeT } from "src/pages/product/product-types";
import { DayOfWeekType } from "src/pages/workOrder/workOrder-types";
import { findDefaultReservationPolicy, findFacilityReservationPolicy } from "../utils/reservationPolicy";
import useDirtyState from "src/recoil/dirty/hooks";
import { useModal } from "src/recoil/modalState/hook";

// ==================== 타입 정의 ====================
type Props = {
  spaceType: SpaceTypeT;
  serviceType: UnionServiceType;
  serviceId: string;
  isDefault: boolean; // 기본설정 or 개별설정
  space?: CommonFacilityModel;
  isRemovable?: boolean;
  isEditable?: boolean;
};

type TimeGroup = {
  name: string;
  label: string;
  startHour: number;
  endHour: number;
};

// 요일 데이터
const DAYS_DATA = [
  { KR: "월요일", EN: DayOfWeekType.MON },
  { KR: "화요일", EN: DayOfWeekType.TUE },
  { KR: "수요일", EN: DayOfWeekType.WED },
  { KR: "목요일", EN: DayOfWeekType.THU },
  { KR: "금요일", EN: DayOfWeekType.FRI },
  { KR: "토요일", EN: DayOfWeekType.SAT },
  { KR: "일요일", EN: DayOfWeekType.SUN },
] as const;

// const DAYS = DAYS_DATA.map((day) => day.KR);

// RRVP03/09 는 요일을 ISO-8601 숫자로 주고받는다(1=월 … 7=일). 화면 스케줄의 키는 "MON" 이라 경계에서 변환한다.
const ISO_DAY_BY_SCHEDULE_KEY = new Map<string, number>(DAYS_DATA.map(({ EN }, index) => [EN, index + 1]));
const SCHEDULE_KEY_BY_ISO_DAY = new Map<number, string>(DAYS_DATA.map(({ EN }, index) => [index + 1, EN]));

const TIME_GROUPS: readonly TimeGroup[] = [
  { name: "새벽", label: "새벽 (00:00 ~ 05:30)", startHour: 0, endHour: 5 },
  { name: "오전", label: "오전 (06:00 ~ 11:30)", startHour: 6, endHour: 11 },
  { name: "오후", label: "오후 (12:00~17:30)", startHour: 12, endHour: 17 },
  { name: "야간", label: "야간 (18:00 ~ 23:30)", startHour: 18, endHour: 23 },
];

// ==================== 유틸리티 함수 ====================
// 30분 단위 시간 슬롯 생성 (시간 문자열과 분 값을 함께 저장)
const generateTimeSlots = (): Array<{ time: string; minutes: number }> => {
  const slots: Array<{ time: string; minutes: number }> = [];
  const HOURS_PER_DAY = 24; // 24시간
  const MINUTES_PER_HOUR = 60; // 60분
  const TIME_INTERVAL = 30; // 30분 단위

  for (let hour = 0; hour < HOURS_PER_DAY; hour++) {
    for (let minute = 0; minute < MINUTES_PER_HOUR; minute += TIME_INTERVAL) {
      const timeString = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      const totalMinutes = hour * 60 + minute;
      slots.push({ time: timeString, minutes: totalMinutes });
    }
  }
  return slots;
};

// 전역 상수로 한 번만 생성 - 00:00 ~ 23:30 까지 30분 단위로 생성
const TIME_SLOTS = generateTimeSlots();

// 초기 스케줄 생성 함수 - 시간과 분 정보를 함께 저장
const createInitialSchedule = (): WeekSchedule => {
  return DAYS_DATA.map(({ EN }) => EN).reduce((weekAcc, day) => {
    // 요일별 시간 슬롯 생성
    const daySchedule = TIME_SLOTS.reduce((dayAcc, { time, minutes }) => {
      return {
        ...dayAcc,
        [time]: {
          checked: false,
          minutes,
        },
      };
    }, {});

    // 요일별 시간 슬롯을 주소에 저장
    return {
      ...weekAcc,
      [day]: daySchedule,
    };
  }, {});
};

// ==================== 메인 컴포넌트 ====================
const ReservationStartTime = ({ spaceType, serviceType, serviceId, isDefault, space, isEditable }: Props) => {
  const { pathname } = useLocation();
  const isProductPage = useMemo(() => pathname.includes("/product"), [pathname]);

  // 상태 관리
  const [isEnabled, setIsEnabled] = useState(false);

  // 공용공간 예약정책 적용방식 확인 > 기본설정 / 개별설정 조회
  const [isExclusive, setIsExclusive] = useState(false);

  // 시간대 그룹 펼치기 상태 > 새벽 / 오전 / 오후 / 야간
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() =>
    TIME_GROUPS.reduce((acc, group) => ({ ...acc, [group.name]: false }), {}),
  );

  // 훅 사용
  const { setBaseModal, setAbstractModalZ1 } = useModal();
  const { setIsDirtyState, isDirtyState } = useDirtyState();

  // 예약 시작 시간 정책 저장 (RRVP04/10)
  const { executeAsync: saveReservationPolicy } = useApiOperation(saveReservationPolicyAsync);

  // 예약 정책 적용 방식(기본/개별) 저장 (RRVP05/11). 정책 본문과 별도 API 로 저장한다
  const { executeAsync: saveReservationPolicyExclusive } = useApiOperation(saveReservationPolicyExclusiveAsync);

  // 예약 시작 시간 정책 조회 (RRVP03/09)
  const { executeAsync: getReservationPolicyList } = useApiOperation(getAllReservationPolicyListAsync);

  // 스케줄 관리 커스텀 훅
  const { timeSchedule, setTimeSchedule, toggleTimeSlot, toggleAllDaySchedule, isDayFullySelected } = useReservationSchedule(
    isEnabled,
    setIsDirtyState,
    createInitialSchedule,
    TIME_SLOTS,
  );

  // API 데이터를 스케줄 형태로 변환
  const convertApiDataToSchedule = (startTimes: StartTimePolicy["startTimes"]) => {
    const initialSchedule = createInitialSchedule();

    // 선택된 시간들을 Set으로 변환 (빠른 검색을 위해). ISO 요일 숫자를 스케줄 키("MON")로 되돌린다
    const selectedTimesByDay = (startTimes ?? []).reduce((acc, dayData) => {
      const scheduleKey = SCHEDULE_KEY_BY_ISO_DAY.get(Number(dayData.dayOfWeek));
      if (!scheduleKey) {
        // 알 수 없는 요일을 조용히 버리면 "설정된 시간 없음" 으로 보이고 그대로 저장 시 기존 정책이 지워진다
        console.warn("[RRVP03/09] 알 수 없는 dayOfWeek:", dayData.dayOfWeek);
        return acc;
      }

      acc[scheduleKey] = new Set((dayData.offsetsInMinutes ?? []).map((minutes) => Number(minutes)));
      return acc;
    }, {} as Record<string, Set<number>>);

    // 함수형 방식으로 새로운 스케줄 생성
    return Object.keys(initialSchedule).reduce((weekAcc, day) => {
      const daySchedule: DaySchedule = Object.keys(initialSchedule[day]).reduce((dayAcc, time) => {
        const timeSlot = initialSchedule[day][time];
        const isSelected = selectedTimesByDay[day]?.has(timeSlot.minutes) || false;

        return {
          ...dayAcc,
          [time]: {
            ...timeSlot,
            checked: isSelected,
          },
        };
      }, {} as DaySchedule);

      return {
        ...weekAcc,
        [day]: daySchedule,
      };
    }, {} as WeekSchedule);
  };

  // 체크된 시간이 있는 시간대 찾기
  const getExpandedGroups = (schedule: WeekSchedule) => {
    const groupsToExpand: Record<string, boolean> = {};

    TIME_GROUPS.forEach((group) => {
      const hasCheckedTime = Object.keys(schedule).some((day) =>
        Object.entries(schedule[day]).some(([time, timeSlot]) => {
          if (!timeSlot.checked) return false;

          const hour = parseInt(time.split(":")[0]);
          return hour >= group.startHour && hour <= group.endHour;
        }),
      );

      groupsToExpand[group.name] = hasCheckedTime;
    });

    return groupsToExpand;
  };

  useEffect(() => {
    Promise.all([
      fetchReservationStartTime(), //
    ]);
  }, []);

  // 공용공간 예약 시작 시간 정책 조회 (RRVP03/09)
  const fetchReservationStartTime = async () => {
    try {
      const policies = await getReservationPolicyList({
        policyType: "start-time-policy",
        ...(isProductPage ? { productId: serviceId } : { contractManageId: serviceId }),
        ...(isDefault ? { facilityType: spaceType } : { facilityId: space?.id }),
      });

      // facilityType 으로 걸러도 같은 타입의 개별 공간 정책이 섞여 올 수 있어 scope 로 한 번 더 고른다
      const policy = isDefault
        ? findDefaultReservationPolicy(policies, "start-time-policy", spaceType)
        : findFacilityReservationPolicy(policies, "start-time-policy", space?.id);

      if (policy) {
        const startTimePolicy = policy.details?.startTimePolicy;
        const { disabled, startTimes, exclusive } = startTimePolicy ?? {};

        // 1. 토글 상태 설정
        setIsEnabled(!disabled);

        // 2. 기본설정 / 개별설정 여부 선택
        setIsExclusive(exclusive === true);

        // 2. 스케줄 데이터 변환 및 설정
        const updatedSchedule = convertApiDataToSchedule(startTimes);
        setTimeSchedule(updatedSchedule);

        // 3. 체크된 시간대 자동 펼치기
        const expandedGroups = getExpandedGroups(updatedSchedule);
        setExpandedGroups(expandedGroups);
        setIsDirtyState(false);
      }
    } catch (error) {
      console.error("데이터 로드 실패:", error);
    }
  };

  // 시간대 그룹 토글
  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  // 시간대별 필터링된 슬롯 가져오기
  const getFilteredTimeSlots = (startHour: number, endHour: number) => {
    return TIME_SLOTS.filter(({ time }) => {
      const hour = parseInt(time.split(":")[0]);
      return hour >= startHour && hour <= endHour;
    }).map(({ time }) => time); // 시간 문자열만 반환 (UI에서 사용)
  };

  // API 포맷으로 변환 (RRVP04/10). 요일은 ISO 숫자, 오프셋은 숫자 배열로 보낸다
  const convertScheduleToApiFormat = (): ReservationPolicySaveParams & { startTimePolicy: { startTimes: NonNullable<StartTimePolicy["startTimes"]> } } => {
    const startTimes = DAYS_DATA.map(({ EN }) => {
      const selectedTimes = Object.entries(timeSchedule[EN] || {})
        .filter(([_, timeSlot]) => timeSlot.checked)
        .map(([_, timeSlot]) => timeSlot.minutes);

      return {
        dayOfWeek: ISO_DAY_BY_SCHEDULE_KEY.get(EN)!,
        offsetsInMinutes: selectedTimes,
      };
    }).filter((dayData) => dayData.offsetsInMinutes.length > 0);

    return {
      ...(isProductPage ? { productId: serviceId } : { contractManageId: serviceId }),
      ...(isDefault ? { facilityType: spaceType } : { facilityId: space?.id }),
      // 적용 방식(exclusive)은 이 body 가 아니라 RRVP05/11 로 따로 저장한다
      startTimePolicy: {
        disabled: !isEnabled,
        startTimes,
      },
    };
  };

  // 저장 핸들러
  const onSaveFacilityStartTime = async () => {
    const requestData = convertScheduleToApiFormat();

    const execute = async () => {
      await saveReservationPolicy(requestData);
      // 적용 방식은 개별 설정 화면에서만 고를 수 있고, RRVP05/11 은 공용공간 ID 가 필수다
      if (!isDefault && space?.id) {
        await saveReservationPolicyExclusive({
          ...(isProductPage ? { productId: serviceId } : { contractManageId: serviceId }),
          policyType: "start-time-policy",
          facilityId: space.id,
          exclusive: isExclusive,
        });
      }
      await fetchReservationStartTime();
    };

    try {
      // 예약 시작 시간을 On 으로 설정하고 시간을 설정하지 않았을 경우 모달 노출
      if (requestData.startTimePolicy.disabled === false && requestData.startTimePolicy.startTimes.length === 0) {
        setBaseModal({
          isOpen: true,
          title: "예약 시작 시간을 설정해주세요.",
          // children: "예약 시작 시간을 설정해주세요.",
          btnRightTitle: "확인",
          onClick: () => {
            setBaseModal({ isOpen: false });
          },
        });
        return;
      }

      setBaseModal({
        isOpen: true,
        title: "저장하시겠습니까?",
        children: "저장 후에는 변경 사항이 즉시 적용됩니다.",
        btnLeftTitle: "취소",
        btnRightTitle: "확인",
        onClick: () => {
          // 정책 본문(RRVP04/10)과 적용 방식(RRVP05/11)이 2단계라, 뒤쪽이 실패해도 화면을 서버 상태로 되돌린다
          execute().catch(() => fetchReservationStartTime());
          setBaseModal({ isOpen: false });
        },
        onClose: () => setBaseModal({ isOpen: false }),
      });
    } catch (error) {
      console.error("저장 실패:", error);
    }
  };

  // 모달 닫기 핸들러
  const handleCloseModal = () => {
    if (isDirtyState) {
      setBaseModal({
        isOpen: true,
        title: "취소하시겠습니까?",
        children: "저장하지 않고 취소하면 입력한 내용이 모두 삭제됩니다.",
        btnLeftTitle: "취소",
        btnRightTitle: "확인",
        onClick: () => {
          setIsDirtyState(false);
          setBaseModal({ isOpen: false });
          setAbstractModalZ1({ isOpen: false });
        },
        onClose: () => setBaseModal({ isOpen: false }),
      });
    } else {
      setIsDirtyState(false);
      setAbstractModalZ1({ isOpen: false });
    }
  };

  return (
    <>
      <section className="base-abstract-modal__contents px30">
        <p className="font14 text-primary3 mt20">• 정해진 시간에만 예약이 가능하도록 예약이 시작될 수 있는 시간을 30분 단위로 설정할 수 있습니다.</p>

        {!isDefault && (
          <>
            <div className="base-abstract-modal__contents-subtitle">
              <span className="required font18 font-weight-semibold">예약 정책 적용 방식</span>
            </div>
            <div className="flex-center h36">
              <BaseRadio
                id={"isPolicyMode_true"}
                name={"isPolicyMode"}
                disabled={!isEditable}
                label="기본설정"
                onChange={() => {
                  setIsExclusive(false);
                }}
                checked={isExclusive === false}
              />
              <BaseTooltip
                className={"mr10 no-wrap"}
                tooltip={
                  (
                    <div>
                      <div>• 예약 기본 정책 설정을 그대로 따릅니다.</div>
                      <div>• 기본 설정이 변경되면 즉시 적용됩니다.</div>
                    </div>
                  ) as any as string
                }
                touchIcon="QUESTION"
              ></BaseTooltip>
              <BaseRadio
                id={"isPolicyMode_false"}
                name={"isPolicyMode"}
                label="개별설정" // 무제한
                disabled={!isEditable}
                onChange={() => {
                  setIsExclusive(true);
                }}
                checked={isExclusive === true}
              />
              <BaseTooltip
                tooltip={
                  (
                    <div>
                      <div>• 예약 기본 정책 설정을 따르지 않고 이 공용 공간에만 적용되는 별도의 예약 정책을 설정합니다.</div>
                      <div>• 기본 설정이 변경되더라도 영향을 받지 않습니다.</div>
                    </div>
                  ) as any as string
                }
                touchIcon="QUESTION"
              ></BaseTooltip>
            </div>
          </>
        )}

        {/* 기능 활성화/비활성화 토글 섹션 */}
        <div className="base-abstract-modal__contents-subtitle">
          <div className="flex-center">
            <span className="font18 font-weight-semibold mr20">예약 시작 시간 설정</span>
            <BaseToggle
              checked={isEnabled}
              onChange={(checked: boolean) => {
                setIsEnabled(checked);
                setIsDirtyState(true);
              }}
            />
          </div>
        </div>

        {/* 시간 설정 테이블 영역 */}
        <div className="mt10">
          {/* 요일 헤더 테이블 - 각 요일별 전체 선택/해제 체크박스 포함 */}
          <table className="reservation-time-table w-100">
            <thead>
              <tr>
                {DAYS_DATA.map(({ KR, EN }) => (
                  <th key={EN} className="day-header">
                    <BaseCheckbox
                      checked={isDayFullySelected(EN)}
                      onChange={() => toggleAllDaySchedule(EN)}
                      disabled={!isEnabled}
                      id={`header-${EN}`}
                      name={`header-${EN}`}
                      label={KR}
                    />
                  </th>
                ))}
              </tr>
            </thead>
          </table>

          {/* 시간대별 그룹들 (새벽, 오전, 오후, 야간) */}
          {TIME_GROUPS.map((group) => (
            <div key={group.name} className="time-group mb10">
              {/* 그룹 헤더 - 클릭시 접기/펼치기 */}
              <div className="time-group-header cursor-pointer py8 border-bottom" onClick={() => toggleGroup(group.name)}>
                <span className={expandedGroups[group.name] ? "ic-chevron-up" : "ic-chevron-down"} />
                <span className="font14 font-weight-semibold">{group.label}</span>
              </div>

              {/* 해당 시간대의 시간 테이블 - 펼쳐진 경우에만 표시 */}
              {expandedGroups[group.name] && (
                <table className="reservation-time-table w-100">
                  <tbody>
                    {/* 해당 시간대에 포함되는 시간 슬롯들을 행으로 렌더링 */}
                    {getFilteredTimeSlots(group.startHour, group.endHour).map((time) => (
                      <tr key={time}>
                        {/* 각 요일별로 체크박스 셀 생성 */}
                        {DAYS_DATA.map(({ EN }) => (
                          <td key={`${EN}-${time}`} className="checkbox-cell">
                            <div className="time-checkbox-wrapper">
                              <BaseCheckbox
                                checked={timeSchedule[EN]?.[time]?.checked || false}
                                onChange={() => toggleTimeSlot(EN, time)}
                                disabled={!isEnabled}
                                id={`${EN}-${time}`}
                                name={`${EN}-${time}`}
                                label={time}
                              />
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* 모달 하단 버튼 영역 */}
      <section className="base-abstract-modal__btn-wrap">
        <BaseButton title="취소" className="color-white" onClick={handleCloseModal} />
        {isEditable && <BaseButton title="저장" onClick={onSaveFacilityStartTime} />}
      </section>
    </>
  );
};

export default ReservationStartTime;
